import {
  BarChart3,
  Bell,
  Building2,
  CalendarDays,
  CheckSquare,
  ClipboardList,
  FileText,
  Gauge,
  HeartPulse,
  Inbox,
  LifeBuoy,
  Megaphone,
  Monitor,
  Network,
  Settings,
  ShieldCheck,
  Timer,
  UserCheck,
  Users,
  type LucideIcon,
} from 'lucide-react';
import type { Role } from '@shared/enums';
import type { PermissionKey, Scope } from '@shared/permissions';
import { paths } from '@/routes/paths';

/** System administrators get a dedicated, smaller navigation tree. */
export function isSystemAdminRole(role: Role | null): boolean {
  return role === 'SUPER_ADMIN' || role === 'ADMIN';
}

/**
 * The navigation tree.
 *
 * Items declare a PERMISSION KEY, never a role list. Adding a role, or granting an
 * existing role a new permission, therefore never means editing this file — which is
 * how navigation and authorization drift apart.
 */

export interface NavItem {
  label: string;
  to: string;
  icon: LucideIcon;
  /** Omit for items everyone signed in can see. */
  permission?: PermissionKey;
  /**
   * Minimum scope required for the item to appear. Without this, a directory page
   * would show to anyone holding the permission at `own` scope — every employee holds
   * `employees.read:own` for their own record, which is not a reason to show them the
   * employee directory.
   */
  scope?: Scope;
  /** Visible when the user holds any one of these permissions (sidebar hubs). */
  anyPermission?: { permission: PermissionKey; scope?: Scope }[];
  /** Shown in the mobile bottom bar (max 5). */
  mobile?: boolean;
}

export interface NavSection {
  id: string;
  label: string;
  items: NavItem[];
}

export const SYSTEM_ADMIN_NAV_SECTIONS: NavSection[] = [
  {
    id: 'overview',
    label: 'Overview',
    items: [{ label: 'Dashboard', to: paths.dashboard, icon: Gauge, mobile: true }],
  },
  {
    id: 'administration',
    label: 'Administration',
    items: [
      { label: 'User Management', to: paths.employees, icon: Users, permission: 'employees.read', scope: 'organization' },
      { label: 'Roles & Permissions', to: paths.roles, icon: ShieldCheck, permission: 'roles.read', scope: 'system' },
      {
        label: 'Departments',
        to: paths.departments,
        icon: Building2,
        permission: 'departments.manage',
        scope: 'organization',
      },
      { label: 'Portal Activity', to: paths.portalActivity, icon: Monitor, permission: 'audit.read', scope: 'system', mobile: true },
      {
        label: 'Notifications',
        to: paths.notificationPolicy,
        icon: Bell,
        permission: 'notifications.configurePolicy',
        scope: 'system',
      },
      {
        label: 'System Settings',
        to: paths.companySettings,
        icon: Settings,
        permission: 'settings.read',
        scope: 'system',
      },
      { label: 'Audit Logs', to: paths.auditLogs, icon: FileText, permission: 'audit.read', scope: 'system' },
      {
        label: 'Support alerts',
        to: paths.supportAlertSettings,
        icon: LifeBuoy,
        permission: 'support.notifications.configure',
        scope: 'system',
      },
    ],
  },
  {
    id: 'support',
    label: 'Support',
    items: [
      {
        label: 'Support',
        to: paths.support,
        icon: LifeBuoy,
        anyPermission: [
          { permission: 'support.tickets.read', scope: 'own' },
          { permission: 'support.tickets.readTechnical', scope: 'system' },
          { permission: 'support.tickets.readHr', scope: 'organization' },
        ],
      },
    ],
  },
  {
    id: 'system',
    label: 'System',
    items: [
      { label: 'System Health', to: paths.systemHealth, icon: HeartPulse },
      { label: 'Announcements', to: paths.announcements, icon: Megaphone, permission: 'announcements.read', scope: 'organization' },
    ],
  },
];

export const NAV_SECTIONS: NavSection[] = [
  {
    id: 'overview',
    label: 'Overview',
    items: [{ label: 'Dashboard', to: paths.dashboard, icon: Gauge, mobile: true }],
  },
  {
    id: 'my-work',
    label: 'My work',
    items: [
      {
        label: 'Time & leave',
        to: paths.time,
        icon: CalendarDays,
        mobile: true,
        anyPermission: [
          { permission: 'attendance.punch' },
          { permission: 'leave.read' },
          { permission: 'holidays.read' },
          { permission: 'leave.configure', scope: 'system' },
        ],
      },
      { label: 'Tasks', to: paths.tasks, icon: CheckSquare, permission: 'tasks.read', mobile: true },
      { label: 'Work reports', to: paths.workReports, icon: ClipboardList, permission: 'workReports.read', mobile: true },
      { label: 'Work sessions', to: paths.workSessions, icon: Timer, permission: 'workSessions.read' },
    ],
  },
  {
    id: 'team',
    label: 'Team',
    items: [
      { label: 'Approvals', to: paths.approvals, icon: Inbox, permission: 'approvals.read', scope: 'assigned', mobile: true },
      { label: 'My team', to: paths.myTeam, icon: UserCheck, permission: 'team.read' },
    ],
  },
  {
    id: 'people',
    label: 'People',
    items: [
      {
        label: 'Employees',
        to: paths.employees,
        icon: Users,
        anyPermission: [
          { permission: 'employees.read', scope: 'reports' },
          { permission: 'documents.read', scope: 'own' },
          { permission: 'onboarding.read', scope: 'own' },
        ],
      },
    ],
  },
  {
    id: 'organization',
    label: 'Organization',
    items: [
      {
        label: 'Organization',
        to: paths.organization,
        icon: Network,
        permission: 'organization.readChart',
      },
      { label: 'Departments', to: paths.departments, icon: Building2, permission: 'departments.read' },
      { label: 'Announcements', to: paths.announcements, icon: Megaphone, permission: 'announcements.read' },
      { label: 'Reports', to: paths.reports, icon: BarChart3, permission: 'analytics.readScoped' },
    ],
  },
  {
    id: 'support',
    label: 'Support',
    items: [
      {
        label: 'Support',
        to: paths.support,
        icon: LifeBuoy,
        anyPermission: [
          { permission: 'support.tickets.read', scope: 'own' },
          { permission: 'support.tickets.readTechnical', scope: 'system' },
          { permission: 'support.tickets.readHr', scope: 'organization' },
        ],
      },
    ],
  },
  {
    id: 'administration',
    label: 'Administration',
    items: [
      { label: 'Company settings', to: paths.companySettings, icon: Settings, permission: 'settings.configure' },
      {
        label: 'Support alerts',
        to: paths.supportAlertSettings,
        icon: LifeBuoy,
        permission: 'support.notifications.configure',
        scope: 'system',
      },
      { label: 'Roles', to: paths.roles, icon: ShieldCheck, permission: 'roles.read' },
      { label: 'Notification policy', to: paths.notificationPolicy, icon: Bell, permission: 'notifications.configurePolicy' },
      { label: 'Audit logs', to: paths.auditLogs, icon: FileText, permission: 'audit.read' },
    ],
  },
];

/** Always visible, outside the permission-filtered sections. */
export const ALWAYS_ITEMS: NavItem[] = [
  { label: 'Notifications', to: paths.notifications, icon: Bell, mobile: true },
  { label: 'Settings', to: paths.settings, icon: Settings },
];

export type CanFn = (key: PermissionKey, scope?: Scope) => boolean;

function isVisible(item: NavItem, can: CanFn): boolean {
  if (item.anyPermission?.length) {
    return item.anyPermission.some((entry) => can(entry.permission, entry.scope));
  }
  return !item.permission || can(item.permission, item.scope);
}

function sectionsForRole(role: Role | null): NavSection[] {
  return isSystemAdminRole(role) ? SYSTEM_ADMIN_NAV_SECTIONS : NAV_SECTIONS;
}

export function visibleSections(can: CanFn, role: Role | null = null): NavSection[] {
  return sectionsForRole(role)
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => isVisible(item, can)),
    }))
    .filter((section) => section.items.length > 0);
}

/** Up to five items for the mobile bottom bar. */
const MOBILE_SLOTS = 5;

/**
 * The bottom bar: at most five destinations, Notifications always one of them.
 *
 * The tail is RESERVED rather than appended-then-truncated. Appending first and
 * slicing afterwards meant the fixed items were the ones that fell off: a manager with
 * approvals filled all five from the sections alone and lost the notification bell —
 * the one item in the bar that carries an unread badge, and so the one most worth
 * keeping. Reserving its slot first makes the guarantee structural.
 *
 * Five is the ceiling because a sixth target on a 375px screen is under 60px wide,
 * and the labels stop fitting before the targets do.
 */
export function mobileItems(can: CanFn, role: Role | null = null): NavItem[] {
  const always = ALWAYS_ITEMS.filter((item) => item.mobile);
  const budget = Math.max(0, MOBILE_SLOTS - always.length);

  const fromSections = sectionsForRole(role)
    .flatMap((section) => section.items)
    .filter((item) => item.mobile && isVisible(item, can));

  return [...fromSections.slice(0, budget), ...always];
}

/** Footer nav for system admins — no duplicate Settings entry in the sidebar. */
export function sidebarFooterItems(can: CanFn, role: Role | null): NavItem[] {
  if (isSystemAdminRole(role)) return [];
  return ALWAYS_ITEMS.filter((item) => isVisible(item, can));
}
